import { useState } from "react";
import { toast } from "sonner";
import { Reveal, MaskLineInView } from "../Reveal";
import { createInquiry } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";
import { validateForm } from "../../lib/validation";
import { ArrowRight } from "lucide-react";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="micro text-charcoal/50">{label}</span>
    <input
      {...props}
      className="mt-3 w-full bg-transparent border-b border-forest/25 py-3 text-lg text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-forest transition-colors duration-300"
    />
  </label>
);

export const Contact = () => {
  const { t } = useLanguage();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", business_name: "", services_needed: "", project_details: "" });
  const [loading, setLoading] = useState(false);
  const [slow, setSlow] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const { isValid, errors } = validateForm(form);

    if (!isValid) {
      const errorMsg = Object.values(errors)[0];
      toast.error(errorMsg || c.toastMissing);
      return;
    }

    setLoading(true);
    // The backend sleeps on Render's free tier and a cold start can run close
    // to a minute. Without a word after a few seconds, a dead-looking button
    // reads as broken and people give up before the request ever lands.
    const slowTimer = setTimeout(() => setSlow(true), 6000);
    try {
      await createInquiry(form);
      toast.success(c.toastSuccess);
      setForm({ name: "", email: "", business_name: "", services_needed: "", project_details: "" });
    } catch (err) {
      const errorMsg = err.response?.data?.message || c.toastError;
      toast.error(errorMsg);
    } finally {
      clearTimeout(slowTimer);
      setSlow(false);
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-form" className="py-24 md:py-36 bg-cream">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <h2 className="font-display text-5xl md:text-7xl tracking-tight text-charcoal">
            <MaskLineInView>{c.heading[0]}</MaskLineInView>
            <MaskLineInView delay={0.1} className="text-forest">{c.heading[1]}</MaskLineInView>
          </h2>
          <p className="mt-8 text-lg text-charcoal/65 max-w-md">{c.intro}</p>
          <a
            href="mailto:hello@bergamotstudio9.com"
            data-testid="contact-email-link"
            className="mt-10 inline-block font-display text-2xl md:text-3xl text-forest btn-underline break-all"
          >
            hello@bergamotstudio9.com
          </a>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-8">
            <Field label={c.name} placeholder={c.phName} value={form.name} onChange={set("name")} data-testid="input-name" />
            <Field label={c.email} type="email" placeholder="jane@brand.com" value={form.email} onChange={set("email")} data-testid="input-email" />
            <Field label={c.business} placeholder={c.phBusiness} value={form.business_name} onChange={set("business_name")} data-testid="input-business" />
            <Field label={c.servicesNeeded} placeholder={c.phServices} value={form.services_needed} onChange={set("services_needed")} data-testid="input-services" />
            <label className="block sm:col-span-2">
              <span className="micro text-charcoal/50">{c.projectDetails}</span>
              <textarea
                data-testid="input-project-details"
                rows={3}
                placeholder={c.phDetails}
                value={form.project_details}
                onChange={set("project_details")}
                className="mt-3 w-full bg-transparent border-b border-forest/25 py-3 text-lg text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-forest transition-colors duration-300 resize-none"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-inquiry-btn"
              className="group sm:col-span-2 mt-4 btn-block justify-center bg-forest text-cream hover:bg-charcoal disabled:opacity-60"
            >
              {loading ? c.sending : c.submit}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            {slow && (
              <p data-testid="slow-send-notice" className="sm:col-span-2 -mt-4 text-sm text-charcoal/50">
                {c.stillSending}
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};
